import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createSlots as _createSlots } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "card"
}
const _hoisted_2 = { class: "field-checkbox mb-2" }
const _hoisted_3 = { for: "events" }
const _hoisted_4 = { class: "calendar-item" }
const _hoisted_5 = ["textContent"]
const _hoisted_6 = ["textContent"]
const _hoisted_7 = { class: "field mt-3" }
const _hoisted_8 = { class: "field mt-3" }
const _hoisted_9 = {
  key: 1,
  class: "p-4 text-center"
}
const _hoisted_10 = { class: "flex justify-content-end" }

import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import Dialog from 'primevue/dialog';
import { useToast } from 'primevue/usetoast';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useDateTime } from '@/composables';
import { THERAPY_EVENT_ICONS, EMIT } from '@/enum';
import { useCalendarBulkAttendanceStore } from '@/store';
import { TherapyEventInterface } from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'CalendarBulkAttendance',
  emits: [EMIT.CANCEL, EMIT.SAVE],
  setup(__props, { emit: __emit }) {

const calendarBulkAttendanceStore = useCalendarBulkAttendanceStore();
const { date, events, bulkAttendanceTitle, therapyTypes } = storeToRefs(calendarBulkAttendanceStore);

/**
 * The data ref object used to create the request payload.
 */
const data = ref({
  checkedEvents: [],
  therapyType: '',
  notes: '',
});

/**
 * The required validation rules.
 */
const rules = {
  checkedEvents: { required },
  therapyType: { required },
  notes: { required },
};

/**
 * Initialize the vuelidate instance.
 */
const v$ = useVuelidate(rules, data);

/**
 * Initialize the instance instance.
 */
const toast = useToast();

/**
 * Throw all errors here for easier access.
 */
const errors = ref(null);

/**
 * Determines whether or not the form has been submitted.
 */
const submitted: boolean = ref(false);

/**
 * Define all the available emit events.
 */
const emit = __emit;

/**
 * Whether or not to display the modal.
 */
const displayModal = ref(true);

/**
 * The check all status indicator.
 */
const checkAllEvents = ref(false);

/**
 * Initialize everything required to build out the component.
 */
onMounted(() => init());

/**
 * check whether or not there are any events available that can be bulk documented.
 */
const hasEvents = computed<boolean>(() => !!events.value.length);

/**
 * Define the request payload.
 */
const payload = computed(() => {
  const value = data.value;

  return {
    event_date: useDateTime().formatDateForApi(date.value),
    therapy_events_ids: value.checkedEvents,
    therapy_result_type_id: value.therapyType,
    notes: value.notes,
  };
});

/**
 * Initialize and render the bulk attendance component with selected events and fields.
 */
const init = () => calendarBulkAttendanceStore.getTherapyResultTypes();

/**
 * Get the field styles based on the specific key.
 */
const fieldStyles: string = (key: string, styles: string) => {
  const validation =
    (v$.value[key].$invalid && submitted.value) || (errors.value && errors.value[key]) ? 'p-invalid' : '';
  const classes = styles ? `${styles} ` : '';
  return `${classes}${validation}`;
};

/**
 * Get a comma separated list of attendees for the provided event.
 */
const attendees: string = (event: TherapyEventInterface) => event.attendees.join(',');

/**
 * Get the specific icon for the event based on the provided event type by enum.
 */
const icon = (event) => THERAPY_EVENT_ICONS[event.therapy_event_type.replaceAll(' ', '_').toUpperCase()];

/**
 * Display the toast message.
 */
const doToast = (error: boolean) => {
  toast.add({
    severity: error ? 'error' : 'success',
    summary: error ? 'Error' : 'Saved',
    detail: error ? error : 'The selected events have been documented.',
    life: 3000,
  });
};

/**
 * After a successful save, close the modal.
 */
const doSave = (response) => (doToast(response), onCancel());

/**
 * Do the following after a failed save request.
 * Set the error ref object with the error response.
 * Display the toast message.
 */
const doFail = (response) => {
  submitted.value = false;
  errors.value = response.data.errors;
  doToast(response.data.message);
};

/**
 * Handle the check/uncheck all event in order to populate the event identifiers collection.
 */
const onCheckAll = (event) => (v$.value.checkedEvents.$model = event ? events.value.map((item) => item.id) : []);

/**
 * Handle a single check event which only has a purpose to diable the check all option.
 */
const onCheck = (event) => (checkAllEvents.value = event.length);

/**
 * Handle the cancellation of the bulk attendance modal.
 */
const onCancel = () => emit('cancel');

/**
 * Handle the saving of the bulk attendance modal.
 */
const onSave = () => {
  submitted.value = true;

  // Don't allow a submit event unless the fields are valid.
  if (v$.value.$invalid) return;

  calendarBulkAttendanceStore
    .bulkAttendance(payload.value)
    .then(() => doSave())
    .catch((response) => doFail(response));

  emit('save');
};

return (_ctx: any,_cache: any) => {
  const _component_Checkbox = _resolveComponent("Checkbox")!
  const _component_Icon = _resolveComponent("Icon")!
  const _component_Dropdown = _resolveComponent("Dropdown")!
  const _component_InputText = _resolveComponent("InputText")!
  const _component_AppButton = _resolveComponent("AppButton")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(Dialog), {
      visible: displayModal.value,
      "onUpdate:visible": _cache[4] || (_cache[4] = ($event: any) => ((displayModal).value = $event)),
      modal: true,
      header: _unref(bulkAttendanceTitle),
      draggable: false,
      breakpoints: { '960px': '75vw', '640px': '150vw' },
      style: { width: '40vw' },
      onHide: onCancel
    }, _createSlots({
      default: _withCtx(() => [
        (hasEvents.value)
          ? (_openBlock(), _createElementBlock("form", _hoisted_1, [
              _createElementVNode("div", {
                class: _normalizeClass(`field border-round-md ${
            fieldStyles('checkedEvents') === 'p-invalid' ? 'border-red' : 'border-white'
          } border-2 border-solid bg-white pt-3 pr-3 pb-1 pl-3 bulk-height overflow-auto`)
              }, [
                _createElementVNode("div", _hoisted_2, [
                  _createVNode(_component_Checkbox, {
                    modelValue: checkAllEvents.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((checkAllEvents).value = $event)),
                    binary: true,
                    disabled: submitted.value,
                    onInput: onCheckAll
                  }, null, 8, ["modelValue", "disabled"]),
                  _cache[5] || (_cache[5] = _createElementVNode("label", { for: "events" }, [
                    _createElementVNode("span", { class: "font-semibold" }, "Select/Deselect All")
                  ], -1))
                ]),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(events), (item, key) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: key,
                    class: "field-checkbox mb-2"
                  }, [
                    _createVNode(_component_Checkbox, {
                      modelValue: _unref(v$).checkedEvents.$model,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).checkedEvents.$model) = $event)),
                      name: "event",
                      value: item.id,
                      disabled: submitted.value,
                      onInput: onCheck
                    }, null, 8, ["modelValue", "value", "disabled"]),
                    _createElementVNode("label", _hoisted_3, [
                      _createElementVNode("div", _hoisted_4, [
                        _createElementVNode("span", {
                          class: "font-semibold",
                          textContent: _toDisplayString(item.timeframe)
                        }, null, 8, _hoisted_5),
                        _createVNode(_component_Icon, {
                          name: icon(item),
                          size: "text-sm",
                          class: "mr-1 ml-1 text-teal-600"
                        }, null, 8, ["name"]),
                        _createElementVNode("span", {
                          textContent: _toDisplayString(attendees(item))
                        }, null, 8, _hoisted_6)
                      ])
                    ])
                  ]))
                }), 128))
              ], 2),
              _createElementVNode("div", _hoisted_7, [
                _cache[6] || (_cache[6] = _createElementVNode("label", {
                  class: "required",
                  for: "attendanceType"
                }, "Attendance Type", -1)),
                _createVNode(_component_Dropdown, {
                  id: "calendar_bulk_attendance_therapy_type",
                  modelValue: _unref(v$).therapyType.$model,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(v$).therapyType.$model) = $event)),
                  options: _unref(therapyTypes).options,
                  "option-label": "name",
                  "option-value": "value",
                  loading: _unref(therapyTypes).loading,
                  placeholder: "Select an Attendance Type",
                  class: _normalizeClass(fieldStyles('therapyType', 'w-full')),
                  disabled: submitted.value
                }, null, 8, ["modelValue", "options", "loading", "class", "disabled"])
              ]),
              _createElementVNode("div", _hoisted_8, [
                _cache[7] || (_cache[7] = _createElementVNode("label", {
                  class: "required",
                  for: "firstName"
                }, "Notes", -1)),
                _createVNode(_component_InputText, {
                  id: "calendar_bulk_notes",
                  modelValue: _unref(v$).notes.$model,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(v$).notes.$model) = $event)),
                  type: "text",
                  placeholder: "Enter Notes",
                  class: _normalizeClass(fieldStyles('notes', 'w-full')),
                  disabled: submitted.value
                }, null, 8, ["modelValue", "class", "disabled"])
              ])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_9, "There are no events available to document"))
      ]),
      _: 2
    }, [
      (hasEvents.value)
        ? {
            name: "footer",
            fn: _withCtx(() => [
              _createElementVNode("div", _hoisted_10, [
                _createVNode(_component_AppButton, {
                  type: "secondary",
                  text: "Cancel",
                  class: "mr-2",
                  disabled: submitted.value,
                  onClick: onCancel
                }, null, 8, ["disabled"]),
                _createVNode(_component_AppButton, {
                  type: "primary",
                  text: "Save",
                  disabled: _unref(v$).$invalid || submitted.value,
                  onClick: onSave
                }, null, 8, ["disabled"])
              ])
            ]),
            key: "0"
          }
        : undefined
    ]), 1032, ["visible", "header"])
  ]))
}
}

})